import React, { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import FooterCard from "../../Components/Forms/FormControl/Common/FooterCard";
import { titleCase } from "../../utils/case";
import { toast } from "react-toastify";
import { getLots } from "../../api/lot.api";
import { createPackage } from "../../api/package.api";
import { getAddOns } from "../../api/addon.api";

const PackageCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [lots, setLots] = useState([]);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [optionalAddOns, setOptionalAddOns] = useState([]);
  const [allAddOns, setAllAddOns] = useState([]);

  const [form, setForm] = useState({
    name: "",
    description: "",
    capacity: "",
    maxLotAllowed: "",
    selectedLots: [],
    standardPrice: "",
    peakPrice: "",
    chargedBy: "flat_fee",
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const parsePricing = (pricing) => {
    if (!pricing) return [];

    if (typeof pricing === "string") {
      try {
        return JSON.parse(pricing);
      } catch {
        return [];
      }
    }

    return Array.isArray(pricing) ? pricing : [];
  };

  const getSeasonAmount = (item, season) => {
    const pricing = parsePricing(item.pricing);

    // Add-ons (flat pricing)
    if (pricing.length && pricing.every((p) => !p.season)) {
      return Number(pricing[0]?.amount || 0);
    }

    const standardAmount = Number(
      pricing.find((p) => p.season === "non_peak")?.amount || 0
    );
    const peakAmount = Number(
      pricing.find((p) => p.season === "peak")?.amount || 0
    );

    return season === "peak"
      ? peakAmount > 0
        ? peakAmount
        : standardAmount
      : standardAmount;
  };

  const getCombinationSums = (items, count, season) => {
    if (!count || count <= 0 || items.length === 0) return [0];

    const amounts = items.map(
      item => getSeasonAmount(item, season) * Number(item.quantity || 1)
    );
    const results = [];

    const combine = (start, combo) => {
      if (combo.length === count) {
        results.push(combo.reduce((sum, amount) => sum + amount, 0));
        return;
      }

      for (let i = start; i < amounts.length; i++) {
        combine(i + 1, [...combo, amounts[i]]);
      }
    };

    combine(0, []);

    return results.length > 0 ? results : [0];
  };

  const getCombinationRange = (items, count) => {
    const nonPeakSums = getCombinationSums(items, count, "non_peak");
    const peakSums = getCombinationSums(items, count, "peak");

    return {
      nonPeakMin: Math.min(...nonPeakSums),
      nonPeakMax: Math.max(...nonPeakSums),
      peakMin: Math.min(...peakSums),
      peakMax: Math.max(...peakSums),
    };
  };

  const formatPriceRange = (min, max) => {
    if (min === max) return `RM ${min.toFixed(2)}`;

    return `RM ${min.toFixed(2)} - RM ${max.toFixed(2)}`;
  };

  const fetchLots = async () => {
    try {
      setLotsLoading(true);

      const res = await getLots({
        page: 1,
        perPage: 1000,
        sortField: "location",
        sortOrder: "ASC",
      });

      setLots(res?.data || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLotsLoading(false);
    }
  };

  const fetchAddOns = async () => {
    try {
      const res = await getAddOns({
        page: 1,
        perPage: 1000,
        status: "active",
      });

      setAllAddOns((res.data || []).map(addOn => ({
        ...addOn,
        totalStock: Number(addOn.totalStock || 0),
      })));
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    fetchLots();
    fetchAddOns();
  }, []);

  const selectedLotIds = form.selectedLots.map((lot) => lot.id);

  const handleLotSelect = (lot, checked) => {
    setForm((prev) => {
      const selectedLots = checked
        ? [...prev.selectedLots, lot]
        : prev.selectedLots.filter((item) => item.id !== lot.id);

      return {
        ...prev,
        selectedLots,
      };
    });

    if (!checked) {
      const remainingLotIds = form.selectedLots
        .filter((item) => item.id !== lot.id)
        .map((item) => item.id);

      setSelectedAddOns((prev) =>
        prev.filter((addOn) =>
          addOn.sourceLotIds?.some((lotId) => remainingLotIds.includes(lotId))
        )
      );
    }
  };

  const availableAddOns = allAddOns.map((addOn) => ({
    addOnId: addOn.id,
    photo: addOn.photo,
    name: addOn.name,
    category: addOn.category,
    inventoryType: addOn.inventoryType,
    pricing: addOn.pricing,
    totalStock: addOn.totalStock,
    minQty: 1,
    maxQty:
      addOn.inventoryType === "asset"
        ? Number(addOn.totalStock || 0)
        : undefined,
  }));

  const handleAddOnSelect = (addOn, checked) => {
    setSelectedAddOns(prev =>
      checked
        ? [...prev, { ...addOn, quantity: addOn.minQty || 1 }]
        : prev.filter(item => item.addOnId !== addOn.addOnId)
    );
  };

  const updateAddOnQuantity = (addOnId, quantity) => {
    setSelectedAddOns(prev =>
      prev.map(item =>
        item.addOnId === addOnId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const handleOptionalAddOnSelect = (addOn, checked) => {
    if (!checked) {
      setOptionalAddOns(prev => prev.filter(item => item.id !== addOn.id));
      return;
    }

    const includedQty = Number(
      selectedAddOns.find(item => Number(item.addOnId) === Number(addOn.id))?.quantity || 0
    );

    const remainingQty =
      addOn.inventoryType === "asset" && addOn.totalStock != null
        ? Math.max(Number(addOn.totalStock) - includedQty, 0)
        : undefined;

    setOptionalAddOns(prev => [
      ...prev,
      {
        ...addOn,
        quantity: remainingQty !== undefined ? Math.min(1, remainingQty) : 1,
      },
    ]);
  };

  const updateOptionalAddOnQuantity = (id, quantity) => {
    setOptionalAddOns(prev =>
      prev.map(item => {
        if (item.id !== id) return item;

        const includedQty = Number(
          selectedAddOns.find(x => Number(x.addOnId) === Number(id))?.quantity || 0
        );

        const maxQty =
          item.inventoryType === "asset"
            ? Math.max(Number(item.totalStock || 0) - includedQty, 0)
            : quantity;

        return {
          ...item,
          quantity: Math.min(Number(quantity), maxQty),
        };
      })
    );
  };

  const maxLotAllowed = form.maxLotAllowed
    ? Number(form.maxLotAllowed)
    : form.selectedLots.length;

  const lotValue = getCombinationRange(form.selectedLots, maxLotAllowed);
  const addOnValue = getCombinationRange(selectedAddOns, selectedAddOns.length);

  const packageValue = {
    nonPeakMin: lotValue.nonPeakMin + addOnValue.nonPeakMin,
    nonPeakMax: lotValue.nonPeakMax + addOnValue.nonPeakMax,
    peakMin: lotValue.peakMin + addOnValue.peakMin,
    peakMax: lotValue.peakMax + addOnValue.peakMax,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!form.name) {
        return toast.error("Package name is required");
      }

      if (form.selectedLots.length === 0) {
        return toast.error("Please select at least one lot");
      }

      if (!maxLotAllowed || maxLotAllowed <= 0) {
        return toast.error("Number of lots is required");
      }

      if (maxLotAllowed > form.selectedLots.length) {
        return toast.error("Number of lots cannot exceed selected lots");
      }

      const invalidAddOn = selectedAddOns.find(addOn => {
        const qty = Number(addOn.quantity || 0);

        return (
          qty < (addOn.minQty || 1) ||
          (addOn.maxQty && qty > addOn.maxQty)
        );
      });

      if (invalidAddOn) {
        return toast.error(
          `${invalidAddOn.name} quantity is outside allowed range`
        );
      }

      const invalidOptional = optionalAddOns.find(addOn => {
        if (addOn.inventoryType !== "asset") return false;

        const includedQty = Number(
          selectedAddOns.find(item => Number(item.addOnId) === Number(addOn.id))?.quantity || 0
        );

        return includedQty + Number(addOn.quantity || 0) > Number(addOn.totalStock || 0);
      });

      if (invalidOptional) {
        return toast.error(`${invalidOptional.name} exceeds available inventory`);
      }

      const payload = {
        name: form.name,
        description: form.description,
        capacity: Number(form.capacity),
        maxLotAllowed: form.maxLotAllowed,
        chargedBy: form.chargedBy,
        items: [
          ...form.selectedLots.map((lot) => ({
            itemType: "lot",
            itemId: lot.id,
            quantity: 1,
            status: "active",
          })),

          ...selectedAddOns.map(addOn => ({
            itemType: "add_on",
            itemId: addOn.addOnId,
            quantity: Number(addOn.quantity || 1),
            selectionType: "included",
            status: "active",
          })),

          ...optionalAddOns.map(addOn => ({
            itemType: "add_on",
            itemId: addOn.id,
            quantity: Number(addOn.quantity || 1),
            selectionType: "optional",
            status: "active",
          })),
        ],
        pricing: [
          {
            season: "non_peak",
            amount: Number(form.standardPrice || 0),
          },
          {
            season: "peak",
            amount: Number(form.peakPrice || 0),
          },
        ],
      };

      setLoading(true);

      await createPackage(payload);

      toast.success("Package created successfully");
      navigate("/packages");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const triggerSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("packages"))}
        title={titleCase(t("new package"))}
        mainTitle={titleCase(t("new package"))}
      />

      <Container fluid>
        <Row>
          <Col>
            <Form innerRef={formRef} onSubmit={handleSubmit}>
              <Card className="mb-4">
                <CardHeader>
                  <H5>{titleCase(t("package details"))}</H5>
                </CardHeader>

                <CardBody>
                  <FormGroup>
                    <Label>{titleCase(t("package name"))}</Label>
                    <Input
                      type="text"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("capacity"))}</Label>

                    <Input
                      type="number"
                      min="0"
                      value={form.capacity}
                      onChange={(e) => handleChange("capacity", e.target.value)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("number of lots"))}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.maxLotAllowed}
                      onChange={(e) =>
                        handleChange("maxLotAllowed", e.target.value)
                      }
                      placeholder={titleCase(t("number of lots in package"))}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{titleCase(t("description"))}</Label>
                    <Input
                      type="textarea"
                      value={form.description}
                      onChange={(e) =>
                        handleChange("description", e.target.value)
                      }
                    />
                  </FormGroup>
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <H5>{titleCase(t("select lots"))}</H5>
                </CardHeader>

                <CardBody>
                  {lotsLoading ? (
                    <p className="text-muted mb-0">
                      {titleCase(t("loading"))}...
                    </p>
                  ) : (
                    <div className="table-responsive">
                      <Table bordered>
                        <thead>
                          <tr>
                            <th>{titleCase(t("select"))}</th>
                            <th>{titleCase(t("lot name"))}</th>
                            <th>{titleCase(t("location"))}</th>
                            <th>{titleCase(t("type"))}</th>
                            <th>{titleCase(t("capacity"))}</th>
                          </tr>
                        </thead>

                        <tbody>
                          {lots.map((lot) => (
                            <tr key={lot.id}>
                              <td>
                                <div className="checkbox checkbox-primary">
                                  <Input
                                    id={`lot-${lot.id}`}
                                    type="checkbox"
                                    checked={selectedLotIds.includes(lot.id)}
                                    onChange={(e) =>
                                      handleLotSelect(lot, e.target.checked)
                                    }
                                  />
                                  <Label for={`lot-${lot.id}`} />
                                </div>
                              </td>
                              <td>{lot.name}</td>
                              <td>{lot.location || "-"}</td>
                              <td>{titleCase(t(lot.type || "permanent"))}</td>
                              <td>{lot.capacity ?? "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <H5>{titleCase(t("select included add ons"))}</H5>
                </CardHeader>

                <CardBody>
                  {allAddOns.length > 0 ? (
                    <div className="table-responsive">
                      <Table bordered>
                        <thead>
                          <tr>
                            <th>{titleCase(t("select"))}</th>
                            <th width="90">{titleCase(t("photo"))}</th>
                            <th>{titleCase(t("name"))}</th>
                            <th>{titleCase(t("category"))}</th>
                            <th>{titleCase(t("inventory type"))}</th>
                            <th>{titleCase(t("minimum quantity"))}</th>
                            <th>{titleCase(t("maximum quantity"))}</th>
                            <th>{titleCase(t("quantity"))}</th>
                          </tr>
                        </thead>

                        <tbody>
                          {availableAddOns.map((addOn) => {
                            const checked = selectedAddOns.some(
                              (item) => item.addOnId === addOn.addOnId
                            );

                            return (
                              <tr key={addOn.addOnId}>
                                <td>
                                  <div className="checkbox checkbox-primary">
                                    <Input
                                      id={`add-on-${addOn.addOnId}`}
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) =>
                                        handleAddOnSelect(
                                          addOn,
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <Label for={`add-on-${addOn.addOnId}`} />
                                  </div>
                                </td>
                                <td>
                                  {addOn.photo ? (
                                    <img
                                      src={`${process.env.REACT_APP_FILES_URL}/${addOn.photo}`}
                                      alt={addOn.name}
                                      style={{
                                        width: 70,
                                        height: 70,
                                        objectFit: "contain",
                                        borderRadius: 8,
                                        border: "1px solid #ddd",
                                      }}
                                    />
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td>{addOn.name}</td>
                                <td>{addOn.category || "-"}</td>
                                <td>
                                  {titleCase(
                                    t(
                                      addOn.inventoryType?.replace(/_/g, " ") ||
                                        "-"
                                    )
                                  )}
                                </td>
                                <td>{addOn.minQty ?? "-"}</td>
                                  <td>{addOn.maxQty ?? "-"}</td>
                                  <td style={{ width: "140px" }}>
                                    {checked && (
                                      <Input
                                        type="number"
                                        min={addOn.minQty || 1}
                                        max={addOn.maxQty || undefined}
                                        value={selectedAddOns.find(x => x.addOnId === addOn.addOnId)?.quantity || addOn.minQty || 1}
                                        onChange={e => updateAddOnQuantity(addOn.addOnId, Number(e.target.value || 1))}
                                      />
                                    )}
                                  </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-muted mb-0">
                      {titleCase(t("no add ons available"))}
                    </p>
                  )}
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <H5>{titleCase(t("select optional add ons"))}</H5>
                </CardHeader>

                <CardBody>
                  <div className="table-responsive">
                    <Table bordered>
                      <thead>
                        <tr>
                          <th>Select</th>
                          <th width="90">Photo</th>
                          <th>Name</th>
                          <th>Category</th>
                          <th>Inventory</th>
                          <th width="120">Balance</th>
                          <th width="120">Max Add On</th>
                        </tr>
                      </thead>

                      <tbody>
                        {allAddOns.map(addOn => {
                          const checked = optionalAddOns.some(x => x.id === addOn.id);
                          const includedQty = Number(
                            selectedAddOns.find(x => Number(x.addOnId) === Number(addOn.id))?.quantity || 0
                          );

                          const balanceQty =
                            addOn.inventoryType === "asset"
                              ? Math.max(Number(addOn.totalStock || 0) - includedQty, 0)
                              : null;

                          return (
                            <tr key={addOn.id}>
                              <td>
                                <div className="checkbox checkbox-primary">
                                  <Input
                                    id={`optional-add-on-${addOn.id}`}
                                    type="checkbox"
                                    checked={checked}
                                    onChange={e => handleOptionalAddOnSelect(addOn, e.target.checked)}
                                  />
                                  <Label for={`optional-add-on-${addOn.id}`} />
                                </div>
                              </td>

                              <td>
                                {addOn.photo ? (
                                  <img
                                    src={`${process.env.REACT_APP_FILES_URL}/${addOn.photo}`}
                                    alt={addOn.name}
                                    style={{ width: 70, height: 70, objectFit: "contain", borderRadius: 8, border: "1px solid #ddd" }}
                                  />
                                ) : "-"}
                              </td>
                              <td>{addOn.name}</td>
                              <td>{addOn.category || "-"}</td>
                              <td>{titleCase(t(addOn.inventoryType?.replace(/_/g, " ")))}</td>
                              <td>
                                {addOn.inventoryType === "asset"
                                  ? `${balanceQty} / ${addOn.totalStock}`
                                  : "-"}
                              </td>
                              <td>
                                {checked && (
                                  <Input
                                    type="number"
                                    min={1}
                                    max={addOn.inventoryType === "asset" ? balanceQty : undefined}
                                    value={optionalAddOns.find(x => x.id === addOn.id)?.quantity || 1}
                                    onChange={e => updateOptionalAddOnQuantity(addOn.id, Number(e.target.value || 1))}
                                  />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>

              <Card className="mb-4">
                <CardHeader>
                  <H5>{titleCase(t("pricing"))}</H5>
                </CardHeader>

                <CardBody>
                  <Row>
                    <Col md="4">
                      <FormGroup className="mb-4">
                        <Label>{titleCase(t("charged by"))}</Label>

                        <Input
                          type="select"
                          value={form.chargedBy}
                          onChange={(e) =>
                            handleChange("chargedBy", e.target.value)
                          }
                        >
                          <option value="flat_fee">
                            {titleCase(t("flat fee"))}
                          </option>

                          <option value="hourly">
                            {titleCase(t("hourly"))}
                          </option>

                          <option value="nightly">
                            {titleCase(t("nightly"))}
                          </option>

                          <option value="daily">
                            {titleCase(t("daily"))}
                          </option>

                          <option value="monthly">
                            {titleCase(t("monthly"))}
                          </option>

                          <option value="yearly">
                            {titleCase(t("yearly"))}
                          </option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label>{titleCase(t("standard price"))}</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.standardPrice}
                          onChange={(e) =>
                            handleChange("standardPrice", e.target.value)
                          }
                        />
                      </FormGroup>
                    </Col>

                    <Col md="4">
                      <FormGroup>
                        <Label>{titleCase(t("peak season price"))}</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.peakPrice}
                          onChange={(e) =>
                            handleChange("peakPrice", e.target.value)
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>


                  <div className="p-3 border rounded bg-light text-dark mt-3">
                    <Row>
                      <Col md="4">
                        <strong>{titleCase(t("lot value"))}</strong>
                        <div>
                          {titleCase(t("standard"))}:{" "}
                          {formatPriceRange(
                            lotValue.nonPeakMin,
                            lotValue.nonPeakMax
                          )}
                        </div>
                        <div>
                          {titleCase(t("peak"))}:{" "}
                          {formatPriceRange(
                            lotValue.peakMin,
                            lotValue.peakMax
                          )}
                        </div>
                      </Col>

                      <Col md="4">
                        <strong>{titleCase(t("add on value"))}</strong>
                        <div>
                          {titleCase(t("standard"))}:{" "}
                          {formatPriceRange(
                            addOnValue.nonPeakMin,
                            addOnValue.nonPeakMax
                          )}
                        </div>
                        <div>
                          {titleCase(t("peak"))}:{" "}
                          {formatPriceRange(
                            addOnValue.peakMin,
                            addOnValue.peakMax
                          )}
                        </div>
                      </Col>

                      <Col md="4">
                        <strong>{titleCase(t("package value"))}</strong>
                        <div>
                          {titleCase(t("standard"))}:{" "}
                          {formatPriceRange(
                            packageValue.nonPeakMin,
                            packageValue.nonPeakMax
                          )}
                        </div>
                        <div>
                          {titleCase(t("peak"))}:{" "}
                          {formatPriceRange(
                            packageValue.peakMin,
                            packageValue.peakMax
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </CardBody>
              </Card>
            </Form>

            <FooterCard
              onSubmit={triggerSubmit}
              onCancel={() => navigate(-1)}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default PackageCreate;